import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../lib/api";
import { ArrowRight, ArrowUpRight, CheckCircle, ChartLine, Lock, MoneyWavy, Headset } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Landing() {
    const [stats, setStats] = useState({ members: 0, surveys_completed: 0, surveys_live: 0, paid_out_usd: 0 });
    useEffect(() => {
        api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    return (
        <div className="bg-paper text-ink min-h-screen">
            <Navbar />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-ink-200">
                <div className="absolute inset-0 grid-bg pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 relative">
                    <div className="grid lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-6" data-testid="hero-eyebrow">USA · CANADA · MEMBERS-ONLY RESEARCH COLLECTIVE</p>
                            <h1 className="h1" data-testid="hero-headline">
                                Get paid<br />
                                <span className="text-respondent">for what you think.</span>
                            </h1>
                            <p className="text-lg text-ink-600 mt-8 max-w-xl leading-relaxed">
                                SocietyIntent pays members in cash for completing short, vetted surveys from real brands. Free to join. Real money. No ad-junk.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link to="/register" className="btn btn-respondent" data-testid="hero-join-btn">
                                    Join free, start earning <ArrowRight size={18} />
                                </Link>
                                <Link to="/register?role=advertiser" className="btn btn-outline" data-testid="hero-advertiser-btn">
                                    I&apos;m a brand <ArrowUpRight size={16} />
                                </Link>
                            </div>
                            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-600">
                                <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-success" />Free forever</span>
                                <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-success" />$10 cashout via PayPal</span>
                                <span className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-success" />Detailed consent, you control everything</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="card-base p-8" data-testid="hero-card">
                                <div className="eyebrow mb-3">LIVE METRICS</div>
                                <div className="grid grid-cols-2 gap-6">
                                    <Metric label="Members" value={stats.members} />
                                    <Metric label="Surveys completed" value={stats.surveys_completed} />
                                    <Metric label="Live studies" value={stats.surveys_live} />
                                    <Metric label="Paid out" value={`$${stats.paid_out_usd?.toLocaleString?.() ?? 0}`} />
                                </div>
                                <div className="mt-6 border-t border-ink-200 pt-4 text-xs font-mono text-ink-600">
                                    SOC.INT://NETWORK · STATUS [ONLINE]
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <p className="eyebrow mb-3">01 · HOW IT WORKS</p>
                            <h2 className="h2 max-w-2xl">Three steps, real money, no nonsense.</h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-0 border border-ink-200">
                        <Step n="01" title="Sign up & verify" body="One minute. We collect basic demographics so we only send you surveys you actually qualify for." />
                        <Step n="02" title="Take vetted surveys" body="Every study is screened by our admin team. No bait-and-switch screen-outs hidden behind 30 questions." />
                        <Step n="03" title="Cash out at $10" body="PayPal, Stripe, or gift cards. We process within 48 hours. 100 points = $1. Zero withdrawal fees." />
                    </div>
                </div>
            </section>

            {/* Why us */}
            <section className="py-24 bg-ink text-paper relative overflow-hidden">
                <div className="noise" />
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
                    <p className="eyebrow mb-3 text-paper/70">02 · WHY SOCIETYINTENT</p>
                    <h2 className="h2 max-w-3xl mb-12">Built for the panel-fatigued — and the brands tired of garbage data.</h2>
                    <div className="grid md:grid-cols-2 gap-12">
                        <Why icon={<MoneyWavy size={28} />} title="Fair pay, not pennies." body="Minimum 20¢ per question. Targeted surveys pay 2× standard rates. Your time has a price." />
                        <Why icon={<Lock size={28} />} title="Consent at the molecule." body="Every email, every SMS, every data point — opt-in, logged with timestamp + IP. Revoke any time." />
                        <Why icon={<ChartLine size={28} />} title="Quality over quotas." body="Attention checks, speed checks, one-vote-per-IP. Brands get clean data. Real members get more of it." />
                        <Why icon={<Headset size={28} />} title="Humans, on call." body="A US/CA support team. No chatbots, no purgatory. We respond in hours, not days." />
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <p className="eyebrow mb-3">03 · MEMBERS</p>
                    <h2 className="h2 max-w-3xl mb-12">What people actually say.</h2>
                    <div className="grid md:grid-cols-3 gap-0 border border-ink-200">
                        <Testimonial
                            quote="First survey site that paid me without making me beg. Cashed out $42 in three weeks doing it on the train."
                            who="Marcus T."
                            where="Brooklyn, NY · member since Feb"
                        />
                        <Testimonial
                            quote="The attention checks are kind of brutal but I get it — the surveys actually feel curated, not random junk."
                            who="Priya R."
                            where="Toronto, ON · member since Mar"
                        />
                        <Testimonial
                            quote="As an advertiser I&apos;ve run on three other panels. Cleanest data we&apos;ve seen, and the dashboard ships CSV in one click."
                            who="Lena V., Acme Insights"
                            where="Brand partner"
                        />
                    </div>
                </div>
            </section>

            {/* For advertisers */}
            <section id="advertisers" className="py-24 bg-cream border-y border-ink-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-3 text-advertiser">04 · FOR BRANDS &amp; RESEARCHERS</p>
                        <h2 className="h2 mb-6">Run a study. <br />Pay only for completes.</h2>
                        <p className="text-ink-600 leading-relaxed mb-8">
                            Real US/CA panel. Advanced builder with branching, screeners, and quotas. We take a flat 20% — no per-seat fees, no ARR commitments, no rep-driven contracts.
                        </p>
                        <Link to="/register?role=advertiser" className="btn btn-advertiser" data-testid="advertiser-cta-btn">
                            Run your first study <ArrowRight size={18} />
                        </Link>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="card-base p-0 overflow-hidden">
                            <div className="px-6 py-4 border-b border-ink-200 flex items-center justify-between">
                                <span className="font-mono text-xs">PRICING_MODEL.CSV</span>
                                <span className="font-mono text-xs text-ink-600">v.2026.02</span>
                            </div>
                            <div className="grid grid-cols-2 divide-x divide-ink-200">
                                <PriceRow label="Per question" value="20 pts ($0.20)" />
                                <PriceRow label="Per survey min." value="100 pts ($1.00)" />
                                <PriceRow label="Targeting premium" value="× 2.0" />
                                <PriceRow label="Platform fee" value="20%" />
                                <PriceRow label="Pay only for" value="Completes" />
                                <PriceRow label="Min. responses" value="1 — no floor" />
                            </div>
                            <div className="px-6 py-5 bg-paper border-t border-ink-200 text-sm text-ink-600">
                                Example: 10-question, 200-response study (untargeted) ={" "}
                                <span className="font-mono text-ink">$500.00 total</span>{" "}
                                ($400 to respondents, $100 platform).
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <p className="eyebrow mb-3">05 · QUESTIONS</p>
                    <h2 className="h2 mb-10">Frequently asked.</h2>
                    <div className="divide-y divide-ink-200 border-y border-ink-200">
                        {[
                            ["Is this really free to join?", "Yes. Free forever. We make money from advertisers, not members."],
                            ["How fast do I get paid?", "Once you hit $10 you can request a cashout. Admin processes within 48 hours via PayPal, Stripe, or gift card."],
                            ["Why do you ask demographics?", "So we never waste your time on a study you don't qualify for. You can edit or delete this data anytime."],
                            ["Are the SMS messages spammy?", "No. SMS is opt-in only and used only when a study you qualify for goes live. You can revoke consent in Profile any time."],
                            ["What if a survey screens me out unfairly?", "Report it. Our admin reviews every flagged study and refunds the advertiser when appropriate."],
                            ["Why USA + Canada only?", "We comply with TCPA, CASL, and pay rules locally. Other countries are coming — join the waitlist."],
                        ].map(([q, a]) => (
                            <details key={q} className="group py-6">
                                <summary className="flex justify-between items-center cursor-pointer list-none">
                                    <span className="text-lg font-semibold pr-6">{q}</span>
                                    <span className="font-mono text-2xl text-ink-600 group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <p className="mt-3 text-ink-600 leading-relaxed">{a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 border-t border-ink-200">
                <div className="max-w-4xl mx-auto text-center px-6">
                    <h2 className="h2 mb-6">Your opinion isn&apos;t free. Stop giving it away.</h2>
                    <Link to="/register" className="btn btn-respondent inline-flex" data-testid="footer-cta-btn">
                        Join SocietyIntent — it&apos;s free <ArrowRight size={18} />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <div className="font-mono text-3xl font-semibold tracking-tight" data-testid={`metric-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                {value}
            </div>
            <div className="text-xs text-ink-600 mt-1 uppercase tracking-wider">{label}</div>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="p-10 border-r border-b border-ink-200 last:border-r-0"
        >
            <div className="font-mono text-respondent text-sm mb-6">{n}</div>
            <h3 className="h3 mb-3">{title}</h3>
            <p className="text-ink-600 text-sm leading-relaxed">{body}</p>
        </motion.div>
    );
}

function Why({ icon, title, body }) {
    return (
        <div className="border-t border-paper/20 pt-8">
            <div className="text-respondent mb-4">{icon}</div>
            <h3 className="h3 text-paper mb-2">{title}</h3>
            <p className="text-paper/70 text-sm leading-relaxed">{body}</p>
        </div>
    );
}

function Testimonial({ quote, who, where }) {
    return (
        <div className="p-10 border-r border-b border-ink-200 last:border-r-0 flex flex-col justify-between min-h-[260px]">
            <p className="text-lg font-medium leading-snug">&ldquo;{quote}&rdquo;</p>
            <div className="mt-8">
                <div className="font-semibold text-sm">{who}</div>
                <div className="font-mono text-xs text-ink-600">{where}</div>
            </div>
        </div>
    );
}

function PriceRow({ label, value }) {
    return (
        <div className="px-6 py-5 border-b border-ink-200">
            <div className="text-xs uppercase tracking-wider text-ink-600 mb-1">{label}</div>
            <div className="font-mono text-lg">{value}</div>
        </div>
    );
}
